import type { GlyphInfo } from '@meitu/x-design-text';
import type { TextRenderHighlight, TextRenderInput, TextRenderSnapshot } from '../../types';
import { findLineIndex } from '../../utils';

type FragmentChild = { text: string; left: number; top: number; bottom: number; width: number; height: number };

export type HighlightFragment = {
    left: number;
    top: number;
    width: number;
    height: number;
    fontSize: number;
    highlight: TextRenderHighlight;
    children: FragmentChild[];
};

type HighlightBox = FragmentChild & {
    byteStart: number;
    byteEnd: number;
    bidiLevel: number;
    lineIndex: number;
    fontSize: number;
    highlight: TextRenderHighlight;
};

/**
 * 判断两个 Highlight 声明是否表达相同视觉。
 *
 * @param first - 第一个 Highlight 声明。
 * @param second - 第二个 Highlight 声明。
 * @returns 可合并为同一连续片段时返回 true。
 */
const highlightsEqual = (first: TextRenderHighlight, second: TextRenderHighlight): boolean =>
    JSON.stringify(first) === JSON.stringify(second);

/**
 * 从排版几何派生按视觉连续段切分的 Highlight 片段。
 *
 * @param input - 当前 Text Render 输入。
 * @param snapshot - 当前文字排版快照。
 * @returns 按视觉连续段切分的 Highlight 片段列表。
 */
export const buildHighlightFragments = (input: TextRenderInput, snapshot: TextRenderSnapshot): HighlightFragment[] => {
    const spans = input.spans.length > 0 ? input.spans : [{ ...input, text: input.text }];
    const spanStyles = spans.map((span, index) => ({ span, range: snapshot.spanRanges[index] }));
    const glyphByPosition = new Map<string, GlyphInfo>();
    const glyphsByLine = snapshot.lineMetrics.map(() => [] as GlyphInfo[]);
    snapshot.glyphInfos.forEach((glyph) => {
        const lineIndex = findLineIndex(snapshot.lineMetrics, glyph.y);
        if (lineIndex < 0) return;
        glyphByPosition.set(`${lineIndex}:${glyph.start}`, glyph);
        glyphsByLine[lineIndex].push(glyph);
    });
    const lineOffsets = snapshot.lineMetrics.map((line, index) => {
        const glyphs = glyphsByLine[index] ?? [];
        return line.byteStart - (glyphs.length ? Math.min(...glyphs.map((glyph) => glyph.start)) : 0);
    });
    const delegated = snapshot.instructions.filter(
        (instruction): instruction is Extract<(typeof snapshot.instructions)[number], { kind: 'delegated' }> =>
            instruction.kind === 'delegated',
    );
    const delegatedPositions = new Set(
        delegated.map((item) => `${findLineIndex(snapshot.lineMetrics, item.y)}:${item.byteStart}`),
    );
    const boxes: Array<Omit<HighlightBox, 'fontSize' | 'highlight'>> = [];
    glyphsByLine.forEach((glyphs, lineIndex) => {
        const line = snapshot.lineMetrics[lineIndex];
        const offset = lineOffsets[lineIndex] ?? 0;
        glyphs.forEach((glyph) => {
            if (delegatedPositions.has(`${lineIndex}:${glyph.start}`)) return;
            boxes.push({
                text: glyph.clusterText,
                left: glyph.x,
                top: line.top,
                bottom: line.bottom,
                width: 0,
                height: line.bottom - line.top,
                byteStart: offset + glyph.start,
                byteEnd: offset + glyph.end,
                bidiLevel: glyph.bidiLevel,
                lineIndex,
            });
        });
    });
    delegated.forEach((item) => {
        const lineIndex = findLineIndex(snapshot.lineMetrics, item.y);
        const line = snapshot.lineMetrics[lineIndex];
        if (!line) return;
        boxes.push({
            text: item.text,
            left: item.x,
            top: line.top,
            bottom: line.bottom,
            width: item.advance,
            height: line.bottom - line.top,
            byteStart: (lineOffsets[lineIndex] ?? 0) + item.byteStart,
            byteEnd: (lineOffsets[lineIndex] ?? 0) + item.byteEnd,
            bidiLevel: glyphByPosition.get(`${lineIndex}:${item.byteStart}`)?.bidiLevel ?? 0,
            lineIndex,
        });
    });
    snapshot.lineMetrics.forEach((line, lineIndex) => {
        const lineBoxes = boxes.filter((box) => box.lineIndex === lineIndex).sort((a, b) => a.left - b.left);
        lineBoxes.forEach((box, index) => {
            if (box.width > 0) return;
            box.width = Math.max(0, (lineBoxes[index + 1]?.left ?? line.rightEdgeX) - box.left);
        });
    });

    const highlighted: HighlightBox[] = [];
    boxes.forEach((box) => {
        spanStyles.forEach(({ range, span }, index) => {
            if (!range || box.byteEnd <= range.start || box.byteStart >= range.end) return;
            const highlight = span.highlight === undefined ? input.highlight : span.highlight;
            if (!highlight || box.width <= 0 || box.text === '\n') return;
            highlighted.push({ ...box, highlight, fontSize: spans[index]?.fontSize ?? input.fontSize });
        });
    });
    const groups: HighlightBox[][] = [];
    highlighted
        .sort((first, second) => first.lineIndex - second.lineIndex || first.left - second.left)
        .forEach((box) => {
            const group = groups.at(-1);
            const first = group?.[0];
            const previous = group?.at(-1);
            if (
                first &&
                previous &&
                first.lineIndex === box.lineIndex &&
                first.bidiLevel === box.bidiLevel &&
                highlightsEqual(first.highlight, box.highlight) &&
                previous.left + previous.width >= box.left
            ) {
                group.push(box);
            } else {
                groups.push([box]);
            }
        });
    return groups.map((group) => {
        const left = Math.min(...group.map((box) => box.left));
        const right = Math.max(...group.map((box) => box.left + box.width));
        return {
            left,
            top: group[0].top,
            width: right - left,
            height: group[0].height,
            fontSize: Math.max(...group.map((box) => box.fontSize)),
            highlight: group[0].highlight,
            children: group.map(({ text, left: childLeft, top, bottom, width, height }) => ({
                text,
                left: childLeft,
                top,
                bottom,
                width,
                height,
            })),
        };
    });
};
